import { useEffect, useRef } from "react";

const BENTUK = {
  TERBUKA: { shape: "○", label: "Masih terbuka", warna: "text-sky-300", bg: "bg-sky-400/10 border-sky-400/30", aria: "Lingkaran kosong: akses masih aktif" },
  TAK_BERTUAN: { shape: "▲", label: "Tanpa pemilik", warna: "text-red-300", bg: "bg-red-500/10 border-red-500/35", aria: "Segitiga: akun tanpa pemilik" },
  TERTUTUP: { shape: "✓", label: "Sudah ditutup", warna: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30", aria: "Centang: akses sudah ditutup" },
  TERLEWAT: { shape: "/", label: "Lewat tenggat", warna: "text-amber-300", bg: "bg-amber-400/10 border-amber-400/30", aria: "Garis miring: melewati batas waktu" },
  SELESAI: { shape: "✓", label: "Selesai", warna: "text-emerald-300", bg: "bg-emerald-500/10 border-emerald-500/30", aria: "Centang: baris sudah selesai dikerjakan" },
};

export function StatusMark({ status, testid }) {
  const s = BENTUK[status] || BENTUK.TERBUKA;
  return (
    <span data-testid={testid} aria-label={s.aria}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs font-medium whitespace-nowrap ${s.bg} ${s.warna}`}>
      <span aria-hidden="true" className="font-data">{s.shape}</span>
      <span>{s.label}</span>
    </span>
  );
}

export function StatusBaris({ status, terlewat }) {
  if (status === "selesai") return <StatusMark status="SELESAI" testid="status-selesai" />;
  if (terlewat) return <StatusMark status="TERLEWAT" testid="status-terlewat" />;
  return (
    <span data-testid={`status-${status}`}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs font-medium bg-secondary text-secondary-foreground whitespace-nowrap">
      <span aria-hidden="true" className="font-data">{status === "baru" ? "○" : "→"}</span>
      <span>{status === "baru" ? "Baru masuk" : "Sudah diteruskan"}</span>
    </span>
  );
}

export function LencanaHistoris({ sumber }) {
  return (
    <span data-testid="lencana-historis"
      className="inline-flex items-center px-2 py-0.5 rounded text-xs border border-dashed border-accent/60 text-accent bg-accent/10">
      Data lama — dari {sumber}
    </span>
  );
}

export function KeadaanKosong({ teks, aksiLabel, onAksi, testid }) {
  return (
    <div data-testid={testid} className="border border-dashed rounded p-10 text-center">
      <p className="text-sm text-muted-foreground">{teks}</p>
      {aksiLabel && (
        <button data-testid={`${testid}-aksi`} onClick={onAksi} className="tombol-utama mt-5 px-4 py-2 rounded text-sm">
          {aksiLabel}
        </button>
      )}
    </div>
  );
}

export function KeadaanGalat({ pesan, onCoba, testid }) {
  return (
    <div data-testid={testid} className="rounded border border-destructive/60 bg-destructive/5 p-6">
      <p className="text-sm text-destructive">{pesan}</p>
      <button data-testid={`${testid}-coba-lagi`} onClick={onCoba} className="tombol-garis mt-4 px-3 py-1.5 rounded text-sm">
        Coba lagi
      </button>
    </div>
  );
}

export function KerangkaBaris({ n = 4, testid }) {
  return (
    <div data-testid={testid} className="space-y-2" aria-label="Memuat">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="h-10 rounded bg-muted animate-pulse" />
      ))}
    </div>
  );
}

export function Angka({ nilai, n, satuan, testid }) {
  if (nilai == null || n === 0)
    return <span data-testid={testid} className="text-sm text-muted-foreground italic">belum ada data (n=0)</span>;
  return (
    <span data-testid={testid} className="font-data tabular">
      {nilai}
      {satuan ? <span className="text-muted-foreground text-sm"> {satuan}</span> : null}
    </span>
  );
}

export function CaraHitung({ teks, testid }) {
  return (
    <details className="mt-1.5" data-testid={testid}>
      <summary className="text-xs text-muted-foreground cursor-pointer underline decoration-dotted" aria-label="Lihat cara hitung">
        cara hitungnya
      </summary>
      <p className="mt-1.5 text-xs text-muted-foreground border-l-2 border-primary/40 pl-2.5">{teks}</p>
    </details>
  );
}

export function JudulHalaman({ judul, sub, testid }) {
  return (
    <div className="mb-8">
      <h1 data-testid={testid} className="text-3xl sm:text-4xl font-bold">{judul}</h1>
      {sub && <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">{sub}</p>}
    </div>
  );
}

export function useTabelKeys(onBuka) {
  const ref = useRef(null);
  const cbRef = useRef(onBuka);
  cbRef.current = onBuka;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (e) => {
      if (e.key === "/") {
        const cari = el.querySelector("input[data-cari]");
        if (cari) { e.preventDefault(); cari.focus(); }
        return;
      }
      if (["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      const rs = Array.from(el.querySelectorAll("tr[data-row]"));
      if (!rs.length) return;
      const idx = rs.findIndex((r) => r === document.activeElement);
      if (e.key === "ArrowDown") { e.preventDefault(); rs[Math.min(idx + 1, rs.length - 1)].focus(); }
      if (e.key === "ArrowUp") { e.preventDefault(); rs[Math.max(idx - 1, 0)].focus(); }
      if (e.key === "Enter" && idx >= 0) { e.preventDefault(); cbRef.current(rs[idx].dataset.row); }
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, []);
  return ref;
}

export const KELAS_TABEL = "w-full text-sm border-collapse";
export const KELAS_TH = "text-left p-2.5 border-b bg-muted/60 font-data text-[11px] uppercase tracking-widest text-muted-foreground font-normal sticky top-0";
export const KELAS_TD = "p-2.5 border-b align-top tabular";
export const KELAS_TR = "hover:bg-secondary/50 focus:bg-secondary focus:outline-none transition-colors duration-150 cursor-pointer";
